/**
 * <strong>Purpose:</strong> <br>
 * Provides the ability to position the icon relative to the text.
 * It adds some margin between icon and text, depending on the current position of the icon relative to text.
 * <br><br>
 * <strong>Contract:</strong> <br>
 * <em>Attach-point:</em> iconAndTextContainerNode, iconNode, textNode <br>
 * This Mixin only makes sense when used with IconMixin and TextMixin. The "iconAndTextContainerNode" attach point
 * should contain the iconNode and textNode.
 *
 * @module mw-form/mixins/property/IconAlignmentMixin
 *
 * @copyright Copyright 2014 The MathWorks, Inc.
 */

define([
    "dojo/_base/declare",
    "dojo/dom-construct",
    "mw-form/mixins/property/TextMixin",
    "mw-form/mixins/property/IconMixin"
], function (declare, domConstruct, TextMixin, IconMixin) {

    
    var IconAlignmentMixin = declare(null, {


        CLASS_NAME_PREFIX: "mwIconAlignment",      


        constructor: function() {

            this.ICON_ALIGNMENT_CONFIGURATION = {
                top: {
                    className: this.CLASS_NAME_PREFIX + "Top",
                    textNodeLocation: "after"
                },
                right: {
                    className: this.CLASS_NAME_PREFIX + "Right",
                    textNodeLocation: "before"
                },
                bottom: {
                    className: this.CLASS_NAME_PREFIX + "Bottom",
                    textNodeLocation: "before"
                },
                left: {
                    className: this.CLASS_NAME_PREFIX + "Left",
                    textNodeLocation: "after"
                }
            };
        },


        /**
         * @property  {string} iconAlignment  Supported Values: left, right, top, bottom
         * @instance
         */
        iconAlignment: "left",

        _supportedValues: ["top", "right", "bottom", "left"],

        postCreate: function () {
            this.inherited(arguments);
            this.domNode.classList.add("mwIconAlignmentMixin");
        },

        // Set the iconNode's position relative to the textNode. Supports this via:
        // 1. Changing the CSS "display" property to "block" and "inline-block" on the textNode
        // 2. Interchanging the positions of the textNode and iconNode
        _setIconAlignmentAttr: function (iconAlignment) {

            if (this.get("iconAlignment") === iconAlignment && this._started) {
                return;
            }

            if (this._supportedValues.indexOf(iconAlignment) === -1) {
                throw new Error("'iconAlignment' property expects one of these values: " +
                    this._supportedValues);
            }

            // Remove the previously applied icon alignment CSS class
            var classNameToRemove = this.ICON_ALIGNMENT_CONFIGURATION[this.get("iconAlignment")].className;
            this.domNode.classList.remove(classNameToRemove);

            // Apply the new icon alignment CSS class
            var classNameToAdd = this.ICON_ALIGNMENT_CONFIGURATION[iconAlignment].className;
            this.domNode.classList.add(classNameToAdd);

            // Move the iconNode to the correct location relative to the textNode
            var textNodeLocation = this.ICON_ALIGNMENT_CONFIGURATION[iconAlignment].textNodeLocation;
            domConstruct.place(this.textNode, this.iconNode, textNodeLocation);

            this._set("iconAlignment", iconAlignment);
        }

    });
    IconAlignmentMixin._dependencies = [
        {mixin: TextMixin, orderDependent: false},
        {mixin: IconMixin, orderDependent: false}
    ];
    return IconAlignmentMixin;

});
